import type { Case } from "./useRealtimeCases";
import { formatParisDate, formatParisTime } from "./parisDateTime";
import { BRAND_CONFIG, detectBrandFromCenter } from "./brandConfig";

export type CallGuide = {
  three_yes_validated: boolean;
  budget_confirmation: "confirmed" | "adjusted" | "not_applicable" | null;
  client_driver: "image" | "cohesion" | "fun" | "budget" | null;
  event_goal: string | null;
  catering_notes: string | null;
  expert_recommendation: boolean;
  decision_maker: string | null;
  decision_date: string | null;
};
export const EMPTY_CALL_GUIDE: CallGuide = {
  three_yes_validated: false,
  budget_confirmation: null,
  client_driver: null,
  event_goal: null,
  catering_notes: null,
  expert_recommendation: false,
  decision_maker: null,
  decision_date: null,
};
export const euros = (amount: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(amount);

export function buildCallRecap(c: Case, guide: CallGuide) {
  const context = getCallScriptContext(c, "");
  const budgetLabels = { confirmed: "Confirmé", adjusted: "À ajuster", not_applicable: "Non applicable" };
  const driverLabels = { image: "Image", cohesion: "Cohésion", fun: "Fun", budget: "Budget" };
  return [
    c.event_date ? `Événement : ${context.eventDate}${context.eventTime ? ` à ${context.eventTime}` : " (heure à préciser)"}` : null,
    context.count != null ? `Participants : ${context.count}` : null,
    c.activities?.length ? `Activités : ${c.activities.join(", ")}` : null,
    c.extras?.length ? `Extras : ${c.extras.join(", ")}` : null,
    guide.three_yes_validated ? "Les 3 oui : validés" : null,
    context.budget ? `Budget : ${context.budget}` : null,
    c.budget != null && c.budget_per_person != null ? `Budget par personne : ${euros(c.budget_per_person)}` : null,
    guide.budget_confirmation ? `Confirmation du budget : ${budgetLabels[guide.budget_confirmation]}` : null,
    guide.client_driver ? `Motivation principale : ${driverLabels[guide.client_driver]}` : null,
    guide.event_goal?.trim() ? `Objectif de l’événement : ${guide.event_goal.trim()}` : null,
    guide.catering_notes?.trim() ? `Restauration : ${guide.catering_notes.trim()}` : null,
    guide.expert_recommendation ? "Recommandation personnalisée : présentée" : null,
    guide.decision_maker?.trim() ? `Décisionnaire : ${guide.decision_maker.trim()}` : null,
    guide.decision_date ? `Décision attendue : ${formatParisDate(`${guide.decision_date}T12:00:00Z`)}` : null,
    c.phone_reminder_date ? `Relance prévue : ${formatParisDate(`${c.phone_reminder_date}T12:00:00Z`)}${c.phone_reminder_time ? ` à ${c.phone_reminder_time.slice(0, 5)}` : ""}` : null,
  ].filter(Boolean).join("\n");
}
const positive = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

export function getCallScriptContext(c: Case, salesperson: string) {
  const count = c.participants ?? c.pax;
  const brandName = BRAND_CONFIG[c.brand || detectBrandFromCenter(c.center)].name;
  const dateKnown = !!c.event_date && Number.isFinite(Date.parse(c.event_date));
  const eventDate = dateKnown
    ? formatParisDate(c.event_date!, { day: "numeric", month: "long", year: "numeric" })
    : "date à préciser";
  const eventTime =
    dateKnown && c.event_time_provided === true ? formatParisTime(c.event_date!) : null;
  const budget = positive(c.budget)
    ? `${euros(c.budget!)} au total`
    : positive(c.budget_per_person)
      ? `${euros(c.budget_per_person!)} par personne`
      : null;
  const event = `${c.company_name ? `l’événement pour ${c.company_name}` : "votre événement"}${dateKnown ? ` le ${eventDate}` : ", dont la date reste à préciser"}${eventTime ? ` à ${eventTime}` : ""}${count != null ? ` pour ${count} personnes` : ""}`;
  const services = [...(c.activities || []), ...(c.extras || [])].join(", ");
  const reminderDate = c.phone_reminder_date
    ? formatParisDate(`${c.phone_reminder_date}T12:00:00Z`, { day: "numeric", month: "long" })
    : null;
  const reminderTime = c.phone_reminder_time?.slice(0, 5) || null;
  return {
    count,
    budget,
    brandName,
    eventDate,
    eventTime,
    services,
    greeting: c.client_first_name?.trim() ? `Bonjour ${c.client_first_name.trim()} ?` : "Bonjour !",
    introduction: `${salesperson ? `Je suis ${salesperson}, votre interlocuteur chez ${brandName}.` : `Je vous appelle de ${brandName}.`} Vous avez deux minutes pour affiner votre projet ?`,
    confirmation: `Votre demande concerne ${event} ?`,
    closing: `Parfait, je vous prépare une proposition sur mesure pour ${event}${services ? `, avec ${services}` : ""}, en reprenant ce que nous venons de définir ensemble.`,
    followUp:
      reminderDate && reminderTime
        ? `On se rappelle le ${reminderDate} à ${reminderTime} pour tout valider ensemble ?`
        : "Quel jour et à quelle heure peut-on se rappeler pour valider la proposition ensemble ?",
  };
}

export function latestActiveQuoteAmount(
  quotes: {
    id: string;
    version: number;
    created_at: string;
    amount_ht?: number | string | null;
    archived_at?: string | null;
  }[],
) {
  const latest = quotes
    .filter((q) => !q.archived_at)
    .sort(
      (a, b) =>
        b.version - a.version ||
        b.created_at.localeCompare(a.created_at) ||
        b.id.localeCompare(a.id),
    )[0];
  if (!latest || latest.amount_ht == null) return null;
  const amount = Number(latest.amount_ht);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}
